/**
 * Manifest payload normalization helpers (#69).
 *
 * The CMS delivers the schedule to the player as either:
 *  - **New shape:** `{ manifest: ScheduleManifest, assets: ManifestAssets }`
 *    (Phase 3 — REST `GET /api/screens/[id]/manifest` and MQTT publish)
 *  - **Legacy shape:** bare `ScheduleManifest` (retained MQTT messages
 *    published before Phase 3, which the broker may replay on reconnect)
 *
 * These helpers detect the shape, extract the `assets` map, and build the
 * `assetUrlMap` consumed by `storeManifestAndPreDownload`.
 */

import { z } from 'zod';
import { sleepWindowSchema } from '@dooh/shared';
import type {
  ScheduleManifest,
  ManifestAssets,
  ManifestAssetEntry,
} from '@dooh/shared';

/**
 * Runtime schema mirroring the `ScheduleManifest` structural type from
 * `@dooh/shared` (`types.ts`). The shared package ships the manifest as a
 * TypeScript `interface` only — there is no exported Zod schema for it — so the
 * player validates the untrusted MQTT/REST payload against this local schema
 * before handing it to the schedule evaluator (#178).
 *
 * Kept permissive on individual slot fields (the evaluator tolerates missing
 * optional fields) but strict on the invariants that would otherwise throw
 * downstream — most importantly `slots` MUST be an array, since
 * `findCurrentSlot` reads `manifest.slots.length`.
 */
/**
 * Bottom ticker strip config (#212). Validated independently of the manifest:
 * a malformed ticker must degrade to "no ticker", never reject the whole
 * manifest (which would blank the schedule).
 */
const tickerSchema = z.object({
  enabled: z.boolean(),
  text: z.string().min(1).max(500),
  bgColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  textColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  font: z.enum(['sans', 'serif', 'mono', 'condensed', 'black', 'impact']),
  direction: z.enum(['rtl', 'ltr', 'up', 'down']),
  speed: z.number().int().min(10).max(300).catch(60),
});

const scheduleSlotSchema = z.object({
  id: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  daysOfWeek: z.array(z.number()),
  startDate: z.string(),
  endDate: z.string(),
  assetIds: z.array(z.string()),
  assetDurations: z.array(z.number().nullable()).optional(),
  loopDuration: z.number(),
  priority: z.number(),
  campaignId: z.string(),
  // #212: ticker of the playlist(s) feeding this slot; malformed → null
  ticker: tickerSchema.nullish().catch(null),
});

const scheduleManifestSchema = z.object({
  version: z.string(),
  validFrom: z.string(),
  validUntil: z.string(),
  timezone: z.string(),
  slots: z.array(scheduleSlotSchema),
  fallbackPlaylistId: z.string(),
  // Invalid/unknown ticker payloads become null instead of failing the manifest
  ticker: tickerSchema.nullish().catch(null),
  // Scheduled sleep window — malformed → null (screen simply never sleeps),
  // same degrade-don't-reject rule as the ticker.
  sleep: sleepWindowSchema.nullish().catch(null),
  // Every sleep window for this screen. MUST be declared: zod strips unknown
  // keys, so omitting it here silently deletes the field on ingest and the
  // player falls back to `sleep`, which carries only the FIRST window — a
  // screen with an overnight window listed second would never sleep.
  sleepWindows: z.array(sleepWindowSchema).optional().catch(undefined),
});

/**
 * Safe fallback returned when a payload fails validation. An empty-`slots`
 * manifest is a valid `ScheduleManifest` that the evaluator treats as "nothing
 * scheduled" — `findCurrentSlot` returns `null` and the player falls back —
 * so a malformed payload degrades gracefully instead of crashing playback.
 */
const EMPTY_MANIFEST: ScheduleManifest = {
  version: '',
  validFrom: '',
  validUntil: '',
  timezone: 'UTC',
  slots: [],
  fallbackPlaylistId: '',
};

/** Normalized manifest payload. `assets` is undefined for legacy messages. */
export interface ManifestPayload {
  manifest: ScheduleManifest;
  assets?: ManifestAssets;
}

/** Entry shape required by the pre-download path. */
export interface AssetUrlMapEntry {
  url: string;
  checksum: string;
  fileSize: number;
  type: 'image' | 'video';
  /** Native media duration in seconds (videos; null for images/unknown) */
  duration: number | null;
}

/**
 * Detect whether a raw payload is the new `{ manifest, assets }` wrapper or a
 * legacy bare `ScheduleManifest`, validate the manifest with Zod, and return a
 * normalized object either way.
 *
 * A bare manifest is itself an object with `slots`, so we distinguish the
 * wrapper by looking for `manifest` *and* `assets` keys (or a `slots` key,
 * which only the bare manifest has).
 *
 * The payload arrives untrusted (MQTT publish or REST body), so a malformed
 * manifest — e.g. `{ slots: null }` — must never reach `findCurrentSlot`,
 * which reads `manifest.slots.length` and would otherwise throw and black out
 * the screen (#178). On validation failure we log and return a safe
 * empty-`slots` manifest with no assets, which the caller treats as "no valid
 * manifest" (playback falls back rather than crashing).
 */
export function normalizeManifestPayload(raw: unknown): ManifestPayload {
  const isWrapper =
    !!raw && typeof raw === 'object' && 'manifest' in raw;
  // New wrapper carries the manifest under `.manifest`; legacy messages are the
  // bare manifest. Assets only ever exist on the wrapper shape.
  const rawManifest = isWrapper
    ? (raw as { manifest: unknown }).manifest
    : raw;
  const rawAssets = isWrapper
    ? (raw as { assets?: ManifestAssets }).assets
    : undefined;

  const parsed = scheduleManifestSchema.safeParse(rawManifest);
  if (!parsed.success) {
    console.warn(
      '[Player] Discarding malformed schedule manifest payload:',
      parsed.error.issues,
    );
    return { manifest: EMPTY_MANIFEST, assets: undefined };
  }

  return { manifest: parsed.data as ScheduleManifest, assets: rawAssets };
}

/**
 * Make an asset URL absolute. The player runs on a different host/port than
 * the CMS, so host-relative URLs (`/api/uploads/...`) must be prefixed with
 * `CMS_API_BASE`. Absolute URLs (http/https) are returned unchanged.
 */
export function normalizeAssetUrl(url: string, apiBase: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('/')) {
    // apiBase has no trailing slash; url starts with one
    return `${apiBase}${url}`;
  }
  // Fallback: treat as relative path
  return `${apiBase}/${url}`;
}

/**
 * Convert the manifest's `assets` record into the `Map` shape consumed by the
 * pre-download path (`storeManifestAndPreDownload`). Host-relative URLs are
 * resolved against `apiBase` so `fetch()` hits the CMS, not the player host.
 */
export function buildAssetUrlMap(
  assets: ManifestAssets | undefined,
  apiBase: string,
): Map<string, AssetUrlMapEntry> {
  const map = new Map<string, AssetUrlMapEntry>();
  if (!assets) return map;

  for (const [assetId, rawEntry] of Object.entries(assets)) {
    const entry = rawEntry as ManifestAssetEntry;
    map.set(assetId, {
      url: normalizeAssetUrl(entry.url, apiBase),
      checksum: entry.checksum,
      fileSize: entry.fileSize,
      type: entry.type,
      duration: entry.duration ?? null,
    });
  }

  return map;
}
